import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Text } from '@consta/uikit/Text'
import classes from './DirectoriesOpen.module.scss'
import { Card } from '@consta/uikit/Card'
import { ChoiceGroup } from '@consta/uikit/ChoiceGroup'
import { IconDrillingRig } from '@consta/icons/IconDrillingRig'
import { IconAreaChart } from '@consta/icons/IconAreaChart'
import { IconMapStroked } from '@consta/icons/IconMapStroked'
import { IconFishboneStroked } from '@consta/icons/IconFishboneStroked'
import { IconLayers } from '@consta/icons/IconLayers'
import { IconAdd } from '@consta/icons/IconAdd'
import { IconGrouping } from '@consta/icons/IconGrouping'
import { IconTrash } from '@consta/icons/IconTrash'
import { IconCheck } from '@consta/icons/IconCheck'
import { IconArrowRight } from '@consta/icons/IconArrowRight'
import { IconArrowDown } from '@consta/icons/IconArrowDown'
import { IconSearchStroked } from '@consta/icons/IconSearchStroked'
import { Button } from '@consta/uikit/Button'
import { TextField } from '@consta/uikit/TextField'
import { Table, TableColumn } from '@consta/uikit/Table'
import { Combobox } from '@consta/uikit/Combobox'
import { Pagination } from '@consta/uikit/Pagination'
import { Lookups } from '../../../services/types/TLookupsServices'
import {
  useGetLookupsQuery, useGetLookupsTypeQuery,
  usePostLookupsMutation,
  usePutLookupsMutation,
  useDeleteLookupsMutation,
  useLazyGetChildrenQuery,
  useImportFromFileMutation
} from '../../../services/lookupsService'
import { IconSave } from '@consta/icons/IconSave'
import { IconUpload } from '@consta/icons/IconUpload'
import { Modal } from '@consta/uikit/Modal'
import { IconClose } from '@consta/icons/IconClose'

type TypeChoiceGroup = {
  label: string;
  icon: any;
  id: number;
  type: 'field' | 'deposit' | 'horizon_code' | 'horizon' | 'layer';
}

const items: TypeChoiceGroup[] = [
  {
    label: 'Месторождения',
    icon: IconDrillingRig,
    id: 1,
    type: 'field'
  },
  {
    label: 'Залежи',
    icon: IconAreaChart,
    id: 2,
    type: 'deposit'
  },
  {
    label: 'Шифр горизонта',
    icon: IconMapStroked,
    id: 3,
    type: 'horizon_code'
  },
  {
    label: 'Горизонты',
    icon: IconFishboneStroked,
    id: 4,
    type: 'horizon'
  },
  {
    label: 'Пласты',
    icon: IconLayers,
    id: 5,
    type: 'layer'
  },
]

interface TableRow {
  uuid?: string;
  name: string;
  parent?: string | null;
  parentObject?: { uuid: string; value: string } | null;
  isNew?: boolean;
  originalName?: string;
  originalParent?: string | null;
  id?: string;
  hasChanges?: boolean;
  isSaving?: boolean;
}

interface TreeNode {
  uuid: string;
  value: string;
  type: 'field' | 'deposit';
  parent: string | null;
  parentObject?: { uuid: string; value: string } | null;
  children: TreeNode[];
  level: number;
  isNew?: boolean;
  originalValue?: string;
  originalParent?: string | null;
  hasChanges?: boolean;
  isSaving?: boolean;
}

interface FieldItem {
  uuid: string;
  value: string;
}

const DirectoriesOpen = () => {
  const [selectedType, setSelectedType] = useState<TypeChoiceGroup>(items[0])
  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [rows, setRows] = useState<TableRow[]>([])
  const [fields, setFields] = useState<FieldItem[]>([])
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const [isAnySaving, setIsAnySaving] = useState<boolean>(false)

  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const pageSize = 9
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [importError, setImportError] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const [importFromFile, { isLoading: isImporting }] = useImportFromFileMutation()

  // Для месторождений (дерево) - получаем только корневые элементы с поиском
  const { data: fieldsData, refetch: refetchFields, isLoading: fieldsLoading } = useGetLookupsQuery(
    {
      type: 'field',
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ordering: '-updated_at',
      search: debouncedSearchValue
    },
    { skip: selectedType.type !== 'field' }
  )

  // Для загрузки детей
  const [fetchChildren] = useLazyGetChildrenQuery()

  // Для залежей (таблица) с пагинацией и поиском
  const { data: depositsData, refetch: refetchDeposits, isLoading: depositsLoading } = useGetLookupsQuery(
    {
      type: 'deposit',
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ordering: '-updated_at',
      search: debouncedSearchValue
    },
    { skip: selectedType.type !== 'deposit' }
  )

  // Для месторождений (для комбобокса в залежах) - загружаем для всех вкладок, где нужны комбобоксы
  const { data: fieldsForCombobox, refetch: refetchFieldsForCombobox } = useGetLookupsTypeQuery(
    { type: 'field' },
    { skip: selectedType.type !== 'deposit' && selectedType.type !== 'field' }
  )

  // Для других типов справочников с пагинацией и поиском
  const { data: otherData, refetch: refetchOther, isLoading: otherLoading } = useGetLookupsQuery(
    {
      type: selectedType.type,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ordering: '-updated_at',
      search: debouncedSearchValue
    },
    { skip: selectedType.type === 'field' || selectedType.type === 'deposit' }
  )

  const [postLookups] = usePostLookupsMutation()
  const [putLookups] = usePutLookupsMutation()
  const [deleteLookups] = useDeleteLookupsMutation()

  const isValidFileType = (file: File): boolean => {
    const allowedExtensions = ['.xlsx', '.xls', '.csv']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    return allowedExtensions.includes(fileExtension)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (isValidFileType(file)) {
        setSelectedFile(file)
        setImportError('')
      } else {
        setImportError('Неподдерживаемый формат файла. Поддерживаются: .xlsx, .xls, .csv')
      }
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      if (isValidFileType(file)) {
        setSelectedFile(file)
        setImportError('')
      } else {
        setImportError('Неподдерживаемый формат файла. Поддерживаются: .xlsx, .xls, .csv')
      }
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setUploadProgress(0)
    setImportError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleImportSubmit = async () => {
    if (!selectedFile) return

    try {
      setUploadProgress(10)

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return prev
          }
          return prev + 10
        })
      }, 200)

      await importFromFile({ file: selectedFile }).unwrap()

      clearInterval(progressInterval)
      setUploadProgress(100)

      setTimeout(() => {
        setIsImportModalOpen(false)
        setSelectedFile(null)
        setUploadProgress(0)
        setImportError('')

        // Обновляем только активные запросы
        try {
          if (selectedType.type === 'field' && typeof refetchFields === 'function') {
            refetchFields()
          }
          if (selectedType.type === 'deposit' && typeof refetchDeposits === 'function') {
            refetchDeposits()
          }
          if (selectedType.type !== 'field' && selectedType.type !== 'deposit' && typeof refetchOther === 'function') {
            refetchOther()
          }
          if (typeof refetchFieldsForCombobox === 'function') {
            refetchFieldsForCombobox()
          }
        } catch (err) {
          console.error('Ошибка при обновлении данных:', err)

        }
      }, 500)
    } catch (error: any) {
      console.error('Ошибка при импорте:', error)
      setImportError(error?.data?.message || 'Произошла ошибка при импорте файла')
      setUploadProgress(0)
    }
  }

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false)
    setSelectedFile(null)
    setUploadProgress(0)
    setImportError('')
    setIsDragOver(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Функция для проверки наличия несохраненных изменений
  const checkUnsavedChanges = (nodes: TreeNode[], tableRows: TableRow[]) => {
    // Проверяем дерево
    const hasTreeChanges = nodes.some(node => {
      if (node.hasChanges && !node.isSaving) return true;
      if (node.children && node.children.length > 0) {
        return node.children.some(child => child.hasChanges && !child.isSaving);
      }
      return false;
    });

    // Проверяем таблицу
    const hasTableChanges = tableRows.some(row => row.hasChanges && !row.isSaving);

    return hasTreeChanges || hasTableChanges;
  };

  // Функция для проверки наличия элементов в процессе сохранения
  const checkAnySaving = (nodes: TreeNode[], tableRows: TableRow[]) => {
    // Проверяем дерево
    const hasTreeSaving = nodes.some(node => {
      if (node.isSaving) return true;
      if (node.children && node.children.length > 0) {
        return node.children.some(child => child.isSaving);
      }
      return false;
    });

    // Проверяем таблицу
    const hasTableSaving = tableRows.some(row => row.isSaving);

    return hasTreeSaving || hasTableSaving;
  };

  // Обновляем состояние при изменении дерева или таблицы
  useEffect(() => {
    setHasUnsavedChanges(checkUnsavedChanges(treeData, rows));
    setIsAnySaving(checkAnySaving(treeData, rows));
  }, [treeData, rows]);

  // Простой debounce для поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
      setPage(1)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchValue])

  // При смене типа обновляем данные
  useEffect(() => {
    console.log('Selected type changed:', selectedType.type);
    setExpandedNodes(new Set());
    setSearchValue('');
    setDebouncedSearchValue('');
    setPage(1);
    setHasUnsavedChanges(false);
    setIsAnySaving(false);

    if (selectedType.type === 'field') {
      refetchFields();
      refetchFieldsForCombobox();
    } else if (selectedType.type === 'deposit') {
      refetchDeposits();
      refetchFieldsForCombobox();
    } else {
      refetchOther();
    }
  }, [selectedType.type, refetchFields, refetchDeposits, refetchFieldsForCombobox, refetchOther]);

  // ОТЛАВЛИВАЕМ ИЗМЕНЕНИЕ СТРАНИЦЫ И ПЕРЕЗАПРАШИВАЕМ ДАННЫЕ
  useEffect(() => {
    console.log('Page changed:', page);
    if (selectedType.type === 'field') {
      refetchFields();
    } else if (selectedType.type === 'deposit') {
      refetchDeposits();
    } else {
      refetchOther();
    }
  }, [page, selectedType.type, refetchFields, refetchDeposits, refetchOther]);

  // ОТЛАВЛИВАЕМ ИЗМЕНЕНИЕ ПОИСКА И ПЕРЕЗАПРАШИВАЕМ ДАННЫЕ
  useEffect(() => {
    console.log('Search changed:', debouncedSearchValue);
    if (selectedType.type === 'field') {
      refetchFields();
    } else if (selectedType.type === 'deposit') {
      refetchDeposits();
    } else {
      refetchOther();
    }
  }, [debouncedSearchValue, selectedType.type, refetchFields, refetchDeposits, refetchOther]);

  // Для комбобокса - загружаем месторождения для всех типов
  useEffect(() => {
    if (fieldsForCombobox) {
      console.log('Fields for combobox raw:', fieldsForCombobox);

      let fieldsList: any[] = [];
      //@ts-ignore
      if (fieldsForCombobox && fieldsForCombobox.field && Array.isArray(fieldsForCombobox.field)) {
        //@ts-ignore
        fieldsList = fieldsForCombobox.field;
      }

      console.log('Fields list for combobox:', fieldsList);

      const flatFields = fieldsList.map((field: any) => ({
        uuid: field.uuid,
        value: field.value
      }));

      console.log('Flat fields for combobox:', flatFields);
      setFields(flatFields);
    }
  }, [fieldsForCombobox]);

  // Строим дерево для месторождений (только корневые узлы) с учетом поиска
  useEffect(() => {
    if (selectedType.type === 'field' && fieldsData) {
      console.log('Building root tree for fields:', fieldsData);

      let fieldsList: any[] = [];

      if (Array.isArray(fieldsData)) {
        fieldsList = fieldsData;
      } else if (fieldsData.results) {
        fieldsList = fieldsData.results;
      }

      const rootNodes: TreeNode[] = fieldsList.map((item: any) => ({
        uuid: item.uuid,
        value: item.value,
        type: 'field',
        parent: null,
        level: 0,
        children: [],
        isNew: false,
        originalValue: item.value,
        originalParent: null,
        hasChanges: false,
        isSaving: false
      }));

      // Добавляем новые элементы в начало массива
      const newNodes = treeData.filter(node => node.isNew);

      console.log('Root nodes:', rootNodes);
      setTreeData([...newNodes, ...rootNodes]);
    }
  }, [fieldsData, selectedType.type]);

  // Функция для загрузки детей узла
  const loadChildren = async (nodeUuid: string) => {
    try {
      console.log(`Loading children for node: ${nodeUuid}`);
      const result = await fetchChildren({ uuid: nodeUuid, limit: 1000 });
      console.log(`Children data for ${nodeUuid}:`, result.data);

      if (result.data && Array.isArray(result.data)) {
        const childrenList = result.data;
        console.log(`Found ${childrenList.length} children`);

        if (childrenList.length > 0) {
          setTreeData(prevTreeData => {
            const updateTreeWithChildren = (nodes: TreeNode[]): TreeNode[] => {
              return nodes.map(node => {
                if (node.uuid === nodeUuid) {
                  if (node.children.length > 0) {
                    return node;
                  }

                  // Получаем существующие новые элементы
                  const existingNewChildren = node.children.filter(child => child.isNew);

                  const childrenNodes: TreeNode[] = childrenList.map((child: any) => ({
                    uuid: child.uuid,
                    value: child.value,
                    type: 'deposit',
                    parent: nodeUuid,
                    level: node.level + 1,
                    children: [],
                    isNew: false,
                    originalValue: child.value,
                    originalParent: nodeUuid,
                    hasChanges: false,
                    isSaving: false
                  }));

                  return {
                    ...node,
                    children: [...existingNewChildren, ...childrenNodes]
                  };
                }
                if (node.children && node.children.length > 0) {
                  return {
                    ...node,
                    children: updateTreeWithChildren(node.children)
                  };
                }
                return node;
              });
            };

            return updateTreeWithChildren(prevTreeData);
          });
        }
      }
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  // Для залежей - формируем данные таблицы
  useEffect(() => {
    if (selectedType.type === 'deposit' && depositsData) {
      console.log('Deposits data:', depositsData);

      let depositsList: any[] = [];

      if (Array.isArray(depositsData)) {
        depositsList = depositsData;
      } else if (depositsData.results) {
        depositsList = depositsData.results;
      }

      const formattedRows = depositsList.map((item: any) => {
        let parentId = null;
        let parentObject = null;

        if (item.parent) {
          if (typeof item.parent === 'object') {
            parentId = item.parent.uuid;
            parentObject = item.parent;
          } else {
            parentId = item.parent;
          }
        }

        return {
          uuid: item.uuid,
          name: item.value,
          parent: parentId,
          parentObject: parentObject,
          id: item.uuid,
          isNew: false,
          originalName: item.value,
          originalParent: parentId,
          hasChanges: false,
          isSaving: false
        };
      });

      // Добавляем новые элементы в начало массива
      const newRows = rows.filter(row => row.isNew);

      console.log('Formatted rows:', formattedRows);
      setRows([...newRows, ...formattedRows]);
    }
  }, [depositsData, selectedType.type]);

  // Для других типов справочников
  useEffect(() => {
    if (selectedType.type !== 'field' && selectedType.type !== 'deposit' && otherData) {
      console.log('Building table for other type:', selectedType.type, otherData);

      let itemsList: any[] = [];

      if (Array.isArray(otherData)) {
        itemsList = otherData;
      } else if (otherData.results) {
        itemsList = otherData.results;
      }

      const formattedRows = itemsList.map((item: any) => ({
        uuid: item.uuid,
        name: item.value,
        parent: item.parent,
        id: item.uuid,
        isNew: false,
        originalName: item.value,
        originalParent: item.parent,
        hasChanges: false,
        isSaving: false
      }));

      // Добавляем новые элементы в начало массива
      const newRows = rows.filter(row => row.isNew);

      setRows([...newRows, ...formattedRows]);
    }
  }, [otherData, selectedType.type]);

  const handleAddClick = () => {
    if (selectedType.type === 'field') {
      const newNode: TreeNode = {
        uuid: `new-${Date.now()}`,
        value: '',
        type: 'field',
        parent: null,
        level: 0,
        children: [],
        isNew: true,
        originalValue: '',
        originalParent: null,
        hasChanges: true,
        isSaving: false
      };
      setTreeData([newNode, ...treeData]);
    } else {
      const newRow: TableRow = {
        id: `new-${Date.now()}`,
        name: '',
        parent: null,
        isNew: true,
        originalName: '',
        originalParent: null,
        hasChanges: true,
        isSaving: false
      };
      setRows([newRow, ...rows]);
    }
  };

  const handleAddDeposit = (fieldUuid: string, fieldValue: string) => {
    const newDeposit: TreeNode = {
      uuid: `new-${Date.now()}`,
      value: '',
      type: 'deposit',
      parent: fieldUuid,
      parentObject: { uuid: fieldUuid, value: fieldValue },
      level: 1,
      children: [],
      isNew: true,
      originalValue: '',
      originalParent: fieldUuid,
      hasChanges: true,
      isSaving: false
    };

    const updateTreeWithNewDeposit = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.uuid === fieldUuid) {
          return {
            ...node,
            children: [newDeposit, ...(node.children || [])]
          };
        }
        if (node.children.length > 0) {
          return {
            ...node,
            children: updateTreeWithNewDeposit(node.children)
          };
        }
        return node;
      });
    };

    setTreeData(updateTreeWithNewDeposit(treeData));
    //@ts-ignore
    setExpandedNodes(prev => new Set([...prev, fieldUuid]));
  };

  const handleSaveField = async (node: TreeNode) => {
    if (!node.value?.trim()) return;
    if (node.type === 'deposit' && !node.parent) {
      console.error('Parent is required for deposit');
      return;
    }

    // Очищаем ошибку
    setFieldErrors(prev => ({ ...prev, [node.uuid]: '' }));

    setTreeData(prevTreeData => {
      const setSavingFlag = (nodes: TreeNode[]): TreeNode[] => {
        return nodes.map(n => {
          if (n.uuid === node.uuid) {
            return { ...n, isSaving: true };
          }
          if (n.children.length > 0) {
            return { ...n, children: setSavingFlag(n.children) };
          }
          return n;
        });
      };
      return setSavingFlag(prevTreeData);
    });

    try {
      const requestBody: any = {
        type: node.type === 'field' ? 'field' : 'deposit',
        value: node.value.trim()
      };

      if (node.type === 'deposit' && node.parent) {
        requestBody.parent = node.parent;
      }

      if (node.isNew) {
        await postLookups(requestBody).unwrap();
      } else if (node.uuid && !node.uuid.startsWith('new-')) {
        await putLookups({
          ...requestBody,
          uuid: node.uuid
        }).unwrap();
      }

      await refetchFields();

      setTreeData(prevTreeData => {
        const resetChanges = (nodes: TreeNode[]): TreeNode[] => {
          return nodes.map(n => ({
            ...n,
            hasChanges: false,
            isNew: false,
            isSaving: false,
            originalValue: n.value,
            originalParent: n.parent,
            children: n.children ? resetChanges(n.children) : []
          }));
        };
        return resetChanges(prevTreeData);
      });

    } catch (error: any) {
      console.error('Ошибка при сохранении:', error);
      // Сохраняем ошибку
      if (error?.data?.value) {
        setFieldErrors(prev => ({ ...prev, [node.uuid]: error.data.value }));
      }

      // Снимаем флаг сохранения
      setTreeData(prevTreeData => {
        const resetSavingFlag = (nodes: TreeNode[]): TreeNode[] => {
          return nodes.map(n => {
            if (n.uuid === node.uuid) {
              return { ...n, isSaving: false };
            }
            if (n.children.length > 0) {
              return { ...n, children: resetSavingFlag(n.children) };
            }
            return n;
          });
        };
        return resetSavingFlag(prevTreeData);
      });
    }
  };

  const handleSaveRow = async (row: TableRow) => {
    if (!row.name?.trim()) return;

    // Очищаем ошибку
    setErrors(prev => ({ ...prev, [row.id || '']: '' }));

    setRows(prevRows =>
      prevRows.map(r =>
        r.id === row.id ? { ...r, isSaving: true } : r
      )
    );

    try {
      const requestBody: any = {
        type: selectedType.type,
        value: row.name.trim()
      };

      if (selectedType.type === 'deposit' && row.parent) {
        requestBody.parent = row.parent;
      }

      if (row.isNew) {
        await postLookups(requestBody).unwrap();
      } else if (row.uuid) {
        await putLookups({
          ...requestBody,
          uuid: row.uuid
        }).unwrap();
      }

      if (selectedType.type === 'deposit') {
        await refetchDeposits();
        await refetchFieldsForCombobox();
      } else {
        await refetchOther();
      }

      setRows(prevRows =>
        prevRows.map(r => ({
          ...r,
          hasChanges: false,
          isNew: false,
          isSaving: false,
          originalName: r.name,
          originalParent: r.parent
        }))
      );

    } catch (error: any) {
      console.error('Ошибка при сохранении:', error);
      // Сохраняем ошибку
      if (error?.data?.value) {
        setErrors(prev => ({ ...prev, [row.id || '']: error.data.value }));
      }

      setRows(prevRows =>
        prevRows.map(r =>
          r.id === row.id ? { ...r, isSaving: false } : r
        )
      );
    }
  };

  const handleDeleteRow = async (id: string | undefined) => {
    if (!id) return;

    const rowToDelete = rows.find(row => row.id === id);
    if (rowToDelete?.uuid && !rowToDelete.uuid.startsWith('new-')) {
      try {
        await deleteLookups({ uuid: rowToDelete.uuid }).unwrap();

        if (selectedType.type === 'deposit') {
          await refetchDeposits();
          await refetchFieldsForCombobox();
        } else {
          await refetchOther();
        }
      } catch (error) {
        console.error('Ошибка при удалении:', error);
      }
    } else {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const handleDeleteNode = async (node: TreeNode) => {
    if (node.uuid.startsWith('new-')) {
      const removeNode = (nodes: TreeNode[]): TreeNode[] => {
        return nodes.filter(n => {
          if (n.uuid === node.uuid) {
            return false;
          }
          if (n.children.length > 0) {
            n.children = removeNode(n.children);
          }
          return true;
        });
      };
      setTreeData(removeNode(treeData));
    } else {
      try {
        await deleteLookups({ uuid: node.uuid }).unwrap();
        await refetchFields();
      } catch (error) {
        console.error('Ошибка при удалении:', error);
      }
    }
  };

  const handleFieldChange = (node: TreeNode, value: string | null) => {
    const updateNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(n => {
        if (n.uuid === node.uuid) {
          const hasChanges = value !== n.originalValue;
          return { ...n, value: value || '', hasChanges };
        }
        if (n.children.length > 0) {
          return { ...n, children: updateNode(n.children) };
        }
        return n;
      });
    };
    setTreeData(updateNode(treeData));
  };

  const handleParentChange = (node: TreeNode, parentId: string | null) => {
    const updateNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(n => {
        if (n.uuid === node.uuid) {
          const hasChanges = parentId !== n.originalParent;
          return { ...n, parent: parentId, hasChanges };
        }
        if (n.children.length > 0) {
          return { ...n, children: updateNode(n.children) };
        }
        return n;
      });
    };
    setTreeData(updateNode(treeData));
  };

  const handleRowChange = (id: string | undefined, field: keyof TableRow, value: any) => {
    if (!id) return;
    const newRows = rows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        const hasChanges =
          (field === 'name' && value !== row.originalName) ||
          (field === 'parent' && value !== row.originalParent);
        return { ...updatedRow, hasChanges };
      }
      return row;
    });
    setRows(newRows);
  };

  const toggleNodeExpand = async (nodeUuid: string) => {
    const node = findNode(treeData, nodeUuid);

    if (node && !expandedNodes.has(nodeUuid) && node.children.length === 0 && !node.isNew) {
      await loadChildren(nodeUuid);
    }

    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeUuid)) {
        newSet.delete(nodeUuid);
      } else {
        newSet.add(nodeUuid);
      }
      return newSet;
    });
  };

  const findNode = (nodes: TreeNode[], uuid: string): TreeNode | null => {
    for (const node of nodes) {
      if (node.uuid === uuid) return node;
      if (node.children.length > 0) {
        const found = findNode(node.children, uuid);
        if (found) return found;
      }
    }
    return null;
  };

  const renderTreeNode = (node: TreeNode) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.uuid);

    return (
      <div key={node.uuid} className={classes.treeNode}>
        <div
          className={classes.treeNodeContent}
          style={{ marginLeft: `${node.level * 24}px` }}
        >
          {node.type === 'field' && (
            <button
              className={classes.expandButton}
              onClick={() => toggleNodeExpand(node.uuid)}
              disabled={node.isSaving}
            >
              {/*@ts-ignore */}
              {isExpanded ? <IconArrowDown size="xs" /> : <IconArrowRight size="xs" />}
            </button>
          )}
          {node.type === 'deposit' && <div className={classes.expandPlaceholder} />}

          {node.type === 'field' ? (
            //@ts-ignore
            <IconDrillingRig size="s" />
          ) : (
            //@ts-ignore
            <IconAreaChart size="s" />
          )}

          {node.type === 'field' ? (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <TextField
                value={node.value}
                onChange={(value) => handleFieldChange(node, value)}
                placeholder="Введите наименование"
                size="s"
                className={classes.treeInput}
                disabled={node.isSaving}
              />
              {fieldErrors[node.uuid] && (
                <Text size="xs" view="alert" style={{ marginTop: 4 }}>
                  {fieldErrors[node.uuid]}
                </Text>
              )}
            </div>
          ) : (
            <>
              <div className={classes.depositEditContainer}>
                <TextField
                  value={node.value}
                  onChange={(value) => handleFieldChange(node, value)}
                  placeholder="Введите наименование залежи"
                  size="s"
                  className={classes.depositNameInput}
                  disabled={node.isSaving}
                />
                <Combobox
                  items={fields}
                  value={fields.find(f => f.uuid === node.parent) || null}
                  onChange={(item) => handleParentChange(node, item?.uuid || null)}
                  getItemLabel={(item) => item.value}
                  getItemKey={(item) => item.uuid}
                  placeholder="Месторождение"
                  size="s"
                  className={classes.depositFieldSelect}
                  disabled={node.isSaving}
                />
              </div>
              {fieldErrors[node.uuid] && (
                <Text size="xs" view="alert" style={{ marginTop: 4 }}>
                  {fieldErrors[node.uuid]}
                </Text>
              )}
            </>
          )}

          <div className={classes.actionButtons}>
            {node.hasChanges && (
              <Button
                size="xs"
                view="ghost"
                iconLeft={IconSave}
                onClick={() => handleSaveField(node)}
                disabled={!node.value?.trim() || (node.type === 'deposit' && !node.parent) || node.isSaving}
                loading={node.isSaving}
              />
            )}
            {node.type === 'field' && (
              <Button
                size="xs"
                view="ghost"
                iconLeft={IconAdd}
                onClick={() => handleAddDeposit(node.uuid, node.value)}
                title="Добавить залежь"
                disabled={hasUnsavedChanges || isAnySaving || node.isSaving}
              />
            )}
            {!node.isNew && (
              <Button
                size="xs"
                view="ghost"
                iconLeft={IconTrash}
                onClick={() => handleDeleteNode(node)}
                disabled={node.isSaving}
              />
            )}
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className={classes.childrenContainer}>
            {node.children.map(child => renderTreeNode(child))}
          </div>
        )}
      </div>
    );
  };
  //@ts-ignore
  const getColumns = (): TableColumn<TableRow>[] => {
    if (selectedType.type === 'deposit') {
      return [
        {
          title: 'Наименование',
          accessor: 'name',
          renderCell: (row: TableRow) => (
            <div>
              <TextField
                value={row.name}
                onChange={(value) => handleRowChange(row.id, 'name', value)}
                placeholder="Введите наименование"
                size="s"
                disabled={row.isSaving}
              />
              {errors[row.id || ''] && (
                <Text size="xs" view="alert" style={{ marginTop: 4 }}>
                  {errors[row.id || '']}
                </Text>
              )}
            </div>
          )
        },
        {
          title: 'Месторождение',
          accessor: 'parent',
          renderCell: (row: TableRow) => {
            let selectedValue = null;
            if (row.parentObject) {
              //@ts-ignore
              selectedValue = row.parentObject;
            } else if (row.parent) {
              //@ts-ignore
              selectedValue = fields.find(f => f.uuid === row.parent) || null;
            }

            return (
              <Combobox
                items={fields}
                value={selectedValue}
                onChange={(item) => handleRowChange(row.id, 'parent', item?.uuid || null)}
                getItemLabel={(item) => item.value}
                getItemKey={(item) => item.uuid}
                placeholder="Выберите месторождение"
                size="s"
                disabled={row.isSaving}
              />
            );
          }
        },
        {
          title: 'Действия',
          width: 100,
          //@ts-ignore
          accessor: 'actions',
          renderCell: (row: TableRow) => (
            <div className={classes.actionButtons}>
              {row.hasChanges && (
                <Button
                  size="xs"
                  view="ghost"
                  iconLeft={IconSave}
                  onClick={() => handleSaveRow(row)}
                  disabled={!row.name?.trim() || row.isSaving}
                  loading={row.isSaving}
                />
              )}
              {!row.isNew && (
                <Button
                  size="xs"
                  view="ghost"
                  iconLeft={IconTrash}
                  onClick={() => handleDeleteRow(row.id)}
                  disabled={row.isSaving}
                />
              )}
            </div>
          )
        }
      ];
    }

    return [
      {
        title: selectedType.type === 'horizon_code' ? 'Шифр горизонта' : 'Наименование',
        accessor: 'name',
        renderCell: (row: TableRow) => (
          <div style={{ width: '100%' }}>
            <TextField
              value={row.name}
              onChange={(value) => handleRowChange(row.id, 'name', value)}
              placeholder={selectedType.type === 'horizon_code' ? 'Введите шифр горизонта' : 'Введите наименование'}
              size="s"
              disabled={row.isSaving}
            />
            {errors[row.id || ''] && (
              <Text size="xs" view="alert" style={{ marginTop: 4 }}>
                {errors[row.id || '']}
              </Text>
            )}
          </div>
        )
      },
      {
        title: 'Действия',
        width: 100,
        //@ts-ignore
        accessor: 'actions',
        renderCell: (row: TableRow) => (
          <div className={classes.actionButtons}>
            {row.hasChanges && (
              <Button
                size="xs"
                view="ghost"
                iconLeft={IconSave}
                onClick={() => handleSaveRow(row)}
                disabled={!row.name?.trim() || row.isSaving}
                loading={row.isSaving}
              />
            )}
            {!row.isNew && (
              <Button
                size="xs"
                view="ghost"
                iconLeft={IconTrash}
                onClick={() => handleDeleteRow(row.id)}
                disabled={row.isSaving}
              />
            )}
          </div>
        )
      }
    ];
  };

  const isLoading = () => {
    if (selectedType.type === 'field') {
      return fieldsLoading;
    } else if (selectedType.type === 'deposit') {
      return depositsLoading;
    }
    return otherLoading;
  };

  const getTotalCount = () => {
    if (selectedType.type === 'field' && fieldsData) {
      return fieldsData.count || 0;
    } else if (selectedType.type === 'deposit' && depositsData) {
      return depositsData.count || 0;
    } else if (selectedType.type !== 'field' && selectedType.type !== 'deposit' && otherData) {
      return otherData.count || 0;
    }
    return 0;
  };

  const totalCount = getTotalCount();
  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Text size='2xl' className={classes.title} weight='bold'>
          {/*@ts-ignore */}
          <IconGrouping />
          Справочник
        </Text>
      </div>
      <div className={classes.main}>
        <div className={classes.choiceGroupWrapper}>
          <ChoiceGroup
            items={items}
            value={selectedType}
            getItemLabel={(item) => item.label}
            getItemIcon={(item) => item.icon}
            onChange={(value) => setSelectedType(value)}
            name="ChoiceGroupDirectories"
          />
        </div>
        <Card className={classes.card}>
          <div className={classes.headerCard}>
            <Text size='l' className={classes.cardTitle}>
              {React.createElement(selectedType.icon, { size: 's', view: 'primary' })}
              {selectedType.label}
            </Text>
            <div className={classes.headerActions}>
              <TextField
                placeholder="Поиск..."
                value={searchValue}
                //@ts-ignore
                onChange={setSearchValue}
                size="s"
                withClearButton
                leftSide={IconSearchStroked}
                className={classes.searchField}
              />
              <Button
                size='s'
                label='Импорт'
                iconLeft={IconUpload}
                onClick={() => setIsImportModalOpen(true)}
                disabled={hasUnsavedChanges || isAnySaving}
              />
              <Button
                size='s'
                label='Добавить'
                iconLeft={IconAdd}
                onClick={handleAddClick}
                disabled={hasUnsavedChanges || isAnySaving}
              />
            </div>
          </div>
          <div className={classes.contentWrapper}>
            {isLoading() ? (
              <div className={classes.loadingState}>Загрузка...</div>
            ) : selectedType.type === 'field' ? (
              <div className={classes.treeWrapper}>
                {treeData.length > 0 ? (
                  <div className={classes.treeContainer}>
                    {treeData.map(node => renderTreeNode(node))}
                  </div>
                ) : (
                  <div className={classes.emptyState}>
                    {searchValue ? 'По вашему запросу ничего не найдено' : 'Нет данных. Нажмите "Добавить" для создания новой записи.'}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className={classes.tableWrapper}>
                  {rows.length > 0 ? (
                    <Table
                      //@ts-ignore
                      columns={getColumns()}
                      //@ts-ignore
                      rows={rows}
                      borderBetweenColumns
                      borderBetweenRows
                      size="s"
                    />
                  ) : (
                    <div className={classes.emptyState}>
                      {searchValue ? 'По вашему запросу ничего не найдено' : 'Нет данных. Нажмите "Добавить" для создания новой записи.'}
                    </div>
                  )}
                </div>

              </>
            )}
          </div>
        </Card>
        {totalPages > 1 && (
          <div className={classes.paginationSection}>
            <Pagination
              items={totalPages}
              value={page}
              onChange={handlePageChange}
              showFirstPage
              showLastPage
              visibleCount={7}
              size='s'
            />
          </div>
        )}
      </div>
      {/* Модалка импорта */}
      <Modal
        isOpen={isImportModalOpen}
        hasOverlay
        onClickOutside={handleCloseImportModal}
        onEsc={handleCloseImportModal}
      >
        <div style={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          minWidth: '500px',
          maxWidth: '600px'
        }}>
          {/* Заголовок */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Text size='xl' weight='bold'>Импорт данных справочника</Text>
            <Button
              size='s'
              iconLeft={IconClose}
              view='clear'
              onClick={handleCloseImportModal}
              disabled={isImporting}
            />
          </div>

          {/* Область drag&drop */}
          <div>
            <Text size='s' view='secondary' style={{ marginBottom: '8px' }} required>
              Файл для импорта *
            </Text>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragOver ? '#0070f0' : '#e0e0e0'}`,
                borderRadius: '8px',
                padding: '32px 20px',
                textAlign: 'center',
                backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                cursor: isImporting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isImporting ? 0.6 : 1,
              }}
            >
              {selectedFile ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text weight="semibold" style={{ wordBreak: 'break-word' }}>
                      {selectedFile.name}
                    </Text>
                    <Text size="s" view="secondary">
                      Размер: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </div>
                  {!isImporting && (
                    <Button
                      size="s"
                      view="clear"
                      iconLeft={IconTrash}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveFile()
                      }}
                      label='Удалить'
                    />
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text weight="semibold">Перетащите файл сюда</Text>
                    <Text size="s" view="secondary">или нажмите для выбора</Text>
                  </div>
                  <Text size="s" view="secondary">
                    Поддерживаемые форматы: .xlsx, .xls, .csv
                  </Text>
                </div>
              )}
            </div>

            {/* Скрытый input для файлов */}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
              accept=".xlsx,.xls,.csv"
              disabled={isImporting}
            />
          </div>

          {/* Прогресс бар */}
          {isImporting && uploadProgress > 0 && (
            <div>
              <Text size="s" view="secondary" style={{ marginBottom: '8px' }}>
                Импорт: {uploadProgress}%
              </Text>
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    backgroundColor: '#0070f0',
                    transition: 'width 0.3s ease',
                    borderRadius: '3px'
                  }}
                />
              </div>
            </div>
          )}

          {/* Ошибка */}
          {importError && (
            <Text size='xs' style={{ color: 'red' }}>{importError}</Text>
          )}

          {/* Кнопки действий */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '16px'
          }}>
            <Button
              label="Отмена"
              view="ghost"
              onClick={handleCloseImportModal}
              disabled={isImporting}
            />
            <Button
              label={isImporting ? "Импорт..." : "Импортировать"}
              onClick={handleImportSubmit}
              disabled={!selectedFile || isImporting}
              loading={isImporting}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DirectoriesOpen;